// Obra › Clima › Reporte: informe de clima en PDF (mes o rango) para justificar demoras ante el comitente,
// y la configuración del reporte automático mensual por mail (se guarda; el envío lo hace el servidor).
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FileDown, Mail, ShieldCheck, Loader2, X } from 'lucide-react';
import R from './reglasClima';
import { useRango, cruceLluviaGente } from './Historial';
import { actividadPorDia } from './fotosObra';
import { diezAnios } from './meteo';
import { generarPdf, logoComoDataUrl } from './climaPdf';
import { guardarConfig } from './configClima';
import { Seccion, Kpi, Semaforo, MESES, Cargando, diaCorto } from './ui';

const finMes = (ym) => new Date(Date.UTC(+ym.slice(0, 4), +ym.slice(5, 7), 0)).toISOString().slice(0, 10);
const mesMas = (ym, n) => new Date(Date.UTC(+ym.slice(0, 4), +ym.slice(5, 7) - 1 + n, 1)).toISOString().slice(0, 7);

function ReporteMensual({ feedId, cfg, onCfg }) {
  const [rm, setRm] = useState(cfg.reporteMensual);
  const [nuevo, setNuevo] = useState('');
  const [guardando, setGuardando] = useState(false);
  useEffect(() => setRm(cfg.reporteMensual), [cfg.reporteMensual]);
  const sucio = JSON.stringify(rm) !== JSON.stringify(cfg.reporteMensual);
  const sumar = () => {
    const e = nuevo.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.info('Ese correo no parece válido'); return; }
    if (!rm.emails.includes(e)) setRm({ ...rm, emails: [...rm.emails, e].slice(0, 10) });
    setNuevo('');
  };
  const guardar = async () => {
    if (rm.activo && !rm.emails.length) { toast.info('Sumá al menos un correo para el reporte mensual'); return; }
    setGuardando(true);
    try { const c = await guardarConfig(feedId, { reporteMensual: rm }); onCfg(c); toast.success(rm.activo ? `Listo: el reporte llega el día ${rm.dia} de cada mes.` : 'Reporte mensual apagado.'); } catch (e) { toast.error(e.message); }
    setGuardando(false);
  };
  return (
    <Seccion titulo={<span className="flex items-center gap-2"><Mail className="w-4 h-4 text-blue-600" />Reporte automático todos los meses</span>} sub="Te mandamos por mail el informe del mes anterior, sin que tengas que entrar">
      <button type="button" role="switch" aria-checked={rm.activo} onClick={() => setRm({ ...rm, activo: !rm.activo })}
        className={`w-full flex items-center justify-between gap-4 rounded-xl border-2 p-3 text-left ${rm.activo ? 'border-emerald-200 bg-emerald-50/60' : 'border-gray-200 bg-gray-50/60'}`}>
        <div>
          <p className="text-sm font-semibold text-gray-900">{rm.activo ? 'Prendido' : 'Apagado'}</p>
          <p className="text-xs text-gray-600">{rm.activo ? `Llega el día ${rm.dia} de cada mes a ${rm.emails.length || 'nadie todavía'}${rm.emails.length === 1 ? ' correo' : rm.emails.length > 1 ? ' correos' : ''}.` : 'Prendelo para recibir el resumen del clima del mes y los días no aptos.'}</p>
        </div>
        <span className={`w-12 h-7 flex items-center rounded-full p-1 transition-colors shrink-0 ${rm.activo ? 'bg-blue-600' : 'bg-gray-300'}`}><span className={`bg-white w-5 h-5 rounded-full shadow transform transition-transform ${rm.activo ? 'translate-x-5' : ''}`} /></span>
      </button>
      {rm.activo && (
        <div className="mt-3 space-y-3">
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1.5">¿A quién se lo mandamos?</p>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {rm.emails.map((e) => (
                <span key={e} className="inline-flex items-center gap-1 text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded-full pl-2.5 pr-1 py-0.5">{e}
                  <button type="button" aria-label={`Quitar ${e}`} onClick={() => setRm({ ...rm, emails: rm.emails.filter((x) => x !== e) })} className="p-0.5 rounded-full hover:bg-blue-100"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="email" value={nuevo} onChange={(e) => setNuevo(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), sumar())} placeholder="nombre@empresa.com" className="flex-1 min-w-0 border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm" aria-label="Correo" />
              <button type="button" onClick={sumar} className="text-sm font-semibold border border-gray-300 rounded-lg px-3 hover:bg-gray-50">Sumar</button>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">Día del mes
            <select value={rm.dia} onChange={(e) => setRm({ ...rm, dia: +e.target.value })} className="border border-gray-300 rounded-lg px-2 py-1 text-sm">{[1, 2, 3, 5, 10, 15].map((d) => <option key={d} value={d}>{d}</option>)}</select>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" className="accent-blue-600" checked={rm.logo} onChange={(e) => setRm({ ...rm, logo: e.target.checked })} />Con el logo de la empresa</label>
        </div>
      )}
      <div className="flex justify-end mt-3">
        <button type="button" disabled={!sucio || guardando} onClick={guardar} className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">{guardando && <Loader2 className="w-4 h-4 animate-spin" />}{sucio ? 'Guardar' : 'Guardado'}</button>
      </div>
      {cfg.origen === 'local' && <p className="text-[11px] text-amber-700 mt-2">Quedó guardado en este navegador; se sincroniza cuando el servidor esté disponible.</p>}
    </Seccion>
  );
}

export default function Reporte({ loc, ixFc, U, hoy, user, empresa, cfg, onCfg }) {
  const [modo, setModo] = useState('mes');
  // los primeros días del mes lo útil es el mes anterior (el que se presenta al comitente)
  const [ym, setYm] = useState(Number(hoy.slice(8)) <= 5 ? mesMas(hoy.slice(0, 7), -1) : hoy.slice(0, 7));
  const [rango, setRango] = useState([R.addDias(hoy, -30), R.addDias(hoy, -1)]);
  const [op, setOp] = useState({ tabla: true, cruce: true, logo: true });
  const desde = modo === 'mes' ? `${ym}-01` : rango[0];
  const hastaPedido = modo === 'mes' ? finMes(ym) : rango[1];
  const hasta = hastaPedido > hoy ? hoy : hastaPedido;
  const { cargando, error, ix, evals, resumen } = useRango({ loc, ixFc, U, hoy, desde, hasta });
  const [ia, setIa] = useState(undefined);
  const [prom, setProm] = useState(null);
  useEffect(() => {
    let vivo = true;
    setIa(undefined);
    actividadPorDia(user, desde, hasta).then((m) => vivo && setIa(m)).catch(() => vivo && setIa(null));
    return () => { vivo = false; };
  }, [user?.identifier, user?.customerId, desde, hasta]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let vivo = true;
    setProm(null);
    if (modo !== 'mes') return undefined;
    diezAnios(loc, hoy).then((d) => vivo && setProm(R.promedioMes(d, +ym.slice(5, 7), U))).catch(() => {});
    return () => { vivo = false; };
  }, [loc, hoy, ym, modo, U]);
  const cruce = useMemo(() => (evals && ia ? cruceLluviaGente(evals, ia) : null), [evals, ia]);
  const [generando, setGenerando] = useState(false);
  const meses = Array.from({ length: 13 }, (_, i) => mesMas(hoy.slice(0, 7), -i));
  const noAptos = evals ? evals.filter((e) => e.estado === 'no' && e.laborable) : [];

  const descargar = async () => {
    setGenerando(true);
    try {
      const logo = op.logo ? await logoComoDataUrl(empresa?.logo) : null;
      const { doc, nombre } = generarPdf({
        obra: user?.Name || user?.identifier || 'Obra', empresa: empresa?.customerName, direccion: loc?.direccion, loc, desde, hasta,
        evals, resumen, ix, ia: op.cruce ? ia : null, cruce: op.cruce ? cruce : null, prom: modo === 'mes' ? prom : null, U, logo, opciones: op,
      });
      doc.save(nombre);
      toast.success('Listo: se descargó el informe en PDF.');
    } catch (e) {
      toast.error('No se pudo armar el PDF: ' + e.message);
    }
    setGenerando(false);
  };

  return (
    <div className="space-y-4 clima-in">
      <div className="rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white p-4 flex items-start gap-3">
        <ShieldCheck className="w-7 h-7 text-emerald-600 shrink-0" />
        <div>
          <p className="text-base font-semibold text-gray-900">Respaldá las demoras por clima ante el comitente</p>
          <p className="text-sm text-gray-700">El informe lista cada día hábil en que no se pudo trabajar por el clima, con su causa (lluvia, tormenta, viento, calor o helada), los datos meteorológicos de la ubicación de la obra, los criterios usados y lugar para las firmas. Sirve para pedir ampliación de plazo.</p>
        </div>
      </div>

      <Seccion titulo="Informe en PDF" sub="Elegí el período y descargalo">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex bg-gray-100 rounded-lg p-1">
            {[['mes', 'Un mes'], ['rango', 'Otro rango']].map(([k, t]) => <button key={k} type="button" onClick={() => setModo(k)} className={`text-sm font-medium px-3 py-1.5 rounded-md ${modo === k ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600'}`}>{t}</button>)}
          </div>
          {modo === 'mes' ? (
            <select value={ym} onChange={(e) => setYm(e.target.value)} className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm" aria-label="Mes">
              {meses.map((m) => <option key={m} value={m}>{R.mayuscula(MESES[+m.slice(5, 7) - 1])} {m.slice(0, 4)}{m === hoy.slice(0, 7) ? ' (en curso)' : ''}</option>)}
            </select>
          ) : (
            <span className="flex items-center gap-1.5 text-sm">
              <input type="date" value={rango[0]} max={hoy} onChange={(e) => e.target.value && setRango([e.target.value, rango[1] < e.target.value ? e.target.value : rango[1]])} className="border border-gray-300 rounded-lg px-2 py-1 text-sm" aria-label="Desde" />
              <span className="text-gray-400">a</span>
              <input type="date" value={rango[1]} max={hoy} onChange={(e) => e.target.value && setRango([rango[0] > e.target.value ? e.target.value : rango[0], e.target.value])} className="border border-gray-300 rounded-lg px-2 py-1 text-sm" aria-label="Hasta" />
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3 text-sm text-gray-700">
          <label className="inline-flex items-center gap-2"><input type="checkbox" className="accent-blue-600" checked={op.tabla} onChange={(e) => setOp({ ...op, tabla: e.target.checked })} />Tabla día por día</label>
          <label className="inline-flex items-center gap-2"><input type="checkbox" className="accent-blue-600" checked={op.cruce} onChange={(e) => setOp({ ...op, cruce: e.target.checked })} />Actividad de la obra (cámara con IA)</label>
          <label className="inline-flex items-center gap-2"><input type="checkbox" className="accent-blue-600" checked={op.logo} onChange={(e) => setOp({ ...op, logo: e.target.checked })} disabled={!empresa?.logo} />Logo de la empresa{!empresa?.logo ? ' (no cargado)' : ''}</label>
        </div>

        {cargando && <Cargando txt="Preparando los datos…" />}
        {error && <p className="text-sm text-rose-700 mt-3">{error}</p>}
        {!cargando && !error && resumen && (
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-4 items-start">
            <div className="space-y-3 min-w-0">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Kpi label="Días hábiles" valor={resumen.habiles} />
                <Kpi tono="verde" label="Aptos" valor={resumen.aptos + resumen.cuidado} sub={`${resumen.cuidado} con cuidado`} />
                <Kpi tono="rojo" label="No aptos" valor={resumen.noAptos} sub={`${resumen.noAptosLluvia} por lluvia`} />
                <Kpi tono="azul" label="Lluvia" valor={`${Math.round(resumen.mm)} mm`} sub={`${resumen.diasLluvia} días de lluvia`} />
              </div>
              {noAptos.length > 0 ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50/60 p-3">
                  <p className="text-sm font-semibold text-rose-800 mb-1.5">Días hábiles no aptos que van en el informe</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                    {noAptos.map((e) => <li key={e.fecha} className="text-xs text-gray-800 flex gap-2"><b className="w-16 shrink-0 capitalize">{diaCorto(e.fecha)}</b><span className="truncate">{e.motivos[0].texto}</span></li>)}
                  </ul>
                </div>
              ) : <p className="text-sm text-gray-600 bg-gray-50 rounded-xl px-3 py-2"><Semaforo estado="apto" small /> En este período no hubo días hábiles no aptos por el clima.</p>}
              {modo === 'mes' && prom && <p className="text-xs text-gray-600">Promedio de {MESES[+ym.slice(5, 7) - 1]} en los últimos {prom.anios} años: {prom.noAptoHabiles.toFixed(1).replace('.', ',')} días hábiles no aptos y {Math.round(prom.mm)} mm.</p>}
              {cruce && op.cruce && <p className="text-xs text-gray-600">Incluye la actividad de la obra: {cruce.menos > 0 ? `los días de lluvia trabajó un ${cruce.menos} % menos gente` : 'los días de lluvia la dotación fue similar'}.</p>}
            </div>
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 flex flex-col items-center text-center gap-3">
              <div className="w-24 h-32 bg-white rounded-md shadow border border-gray-200 relative overflow-hidden" aria-hidden="true">
                <div className="h-1.5 bg-[#2BE88B]" />
                <div className="p-2 space-y-1">
                  <div className="h-1.5 w-12 bg-gray-800 rounded" /><div className="h-1 w-16 bg-gray-300 rounded" />
                  <div className="grid grid-cols-2 gap-0.5 pt-1">{[0, 1, 2, 3].map((i) => <div key={i} className={`h-3 rounded-sm ${i === 3 ? 'bg-rose-100' : 'bg-gray-100'}`} />)}</div>
                  <div className="h-5 rounded-sm border border-rose-300 bg-rose-50" />
                  <div className="flex items-end gap-px h-5">{[3, 6, 1, 8, 2, 5, 9, 4].map((h, i) => <div key={i} className="flex-1 bg-sky-500" style={{ height: `${h * 10}%` }} />)}</div>
                </div>
              </div>
              <p className="text-xs text-gray-600">{`${diaCorto(desde)} al ${diaCorto(hasta)}`} · {op.tabla ? '4' : '3'}+ páginas</p>
              <button type="button" onClick={descargar} disabled={generando || ia === undefined} className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold rounded-xl px-4 py-2.5 bg-gray-900 hover:bg-black text-white disabled:opacity-60">
                {generando ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}{ia === undefined ? 'Juntando datos de la obra…' : 'Descargar PDF'}
              </button>
            </div>
          </div>
        )}
      </Seccion>

      <ReporteMensual feedId={user?.feedID} cfg={cfg} onCfg={onCfg} />
    </div>
  );
}
